/**
 * Jak dlouho zlevněná cena platí. Jediné místo, které to říká — ceník, pokladna, teaser i upgrade
 * modal berou popisky odsud, aby si každá obrazovka neodpovídala jinak („/ 1. měsíc“ vs. „/ měsíc“).
 */

/** Trvání kupónu podle Stripe: jednorázově, po N měsíců, nebo trvale. */
export type DiscountDuration = 'ONCE' | 'REPEATING' | 'FOREVER';

export interface DiscountTerms {
    duration?: DiscountDuration | null;
    durationInMonths?: number | null;
}

export interface DiscountPresentation {
    /** Klíč jednotky za zlevněnou cenou („/ 1. měsíc“ nebo „/ měsíc“). */
    unitKey: string;
    /** Klíč dovětku pod cenou (co platí poté); `null`, když sleva trvá napořád. */
    noteKey: string | null;
    /** Počet měsíců pro dovětek opakované slevy; jinak `null`. */
    months: number | null;
}

/** Cena, jak ji posílá API: `price` už po slevě, `originalPrice` před ní. */
export interface PricedItem {
    price: number;
    originalPrice?: number | null;
}

/** Sleva se pozná z čísel z API, ne z režimu obrazovky — backend ji uplatňuje jen tam, kde smí. */
export function priceHasDiscount(price: PricedItem): boolean {
    return price.originalPrice != null && price.originalPrice > price.price;
}

/** Popisky ke zlevněné ceně; `null`, když cena zlevněná není. */
export function pricePresentation(price: PricedItem, discount?: DiscountTerms | null): DiscountPresentation | null {
    return priceHasDiscount(price) ? presentDiscount(discount) : null;
}

/** Výše slevy, jak ji posílá backend: buď procenta, nebo pevná částka v měně zobrazené ceny. */
export interface DiscountAmount {
    percentOff?: number | null;
    amountOff?: number | null;
}

/**
 * Výše slevy jedním zápisem pro všechny obrazovky: procento podle typografie jazyka („50 %“ česky,
 * „50%“ anglicky), pevná částka týmž formátovačem jako ceny. Texty dostávají hotovou hodnotu,
 * takže se znak procenta nepíše do překladů.
 */
export function formatDiscountValue(discount: DiscountAmount | null | undefined, language: string,
                                    formatAmount: (amount: number) => string): string {
    if (discount?.percentOff) {
        return new Intl.NumberFormat(language, {style: 'percent', maximumFractionDigits: 2}).format(discount.percentOff / 100);
    }
    if (discount?.amountOff) {
        return formatAmount(discount.amountOff);
    }
    return '';
}

export const PER_MONTH_KEY = 'subscription.price.per.month';
const FIRST_MONTH_KEY = 'subscription.price.discount.unit.first-month';
const ONCE_NOTE_KEY = 'subscription.price.discount.note.once';
const REPEATING_NOTE_KEY = 'subscription.price.discount.note.repeating';

/**
 * Neznámé trvání (starší záznam, Stripe neodpověděl) se čte jako jednorázové: slíbit zákazníkovi
 * méně a pak účtovat méně je bezpečnější než opak.
 */
export function presentDiscount(terms?: DiscountTerms | null): DiscountPresentation {
    if (terms?.duration === 'FOREVER') {
        return {unitKey: PER_MONTH_KEY, noteKey: null, months: null};
    }
    const months = terms?.durationInMonths ?? null;
    if (terms?.duration === 'REPEATING' && months != null && months > 1) {
        return {unitKey: PER_MONTH_KEY, noteKey: REPEATING_NOTE_KEY, months};
    }
    return {unitKey: FIRST_MONTH_KEY, noteKey: ONCE_NOTE_KEY, months: null};
}
